import { Typography, Divider, Button, Grid, Alert } from '@rbx/ui';
import React, { FC, useMemo, Fragment } from 'react';
import { useLocalization, useTranslation } from '@rbx/intl';
import { useRobloxAuthentication } from '@rbx/auth';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { TestId } from '@modules/utilities/constants/testConstants';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useConversationMessageStyles from './ConversationMessage.styles';
import { ConversationMessageByBot, ConversationMessageByUser } from './ConversationMessage';
import { useAssistantOptIn, OptInState } from '../context/AssistantOptInProvider';
import { tosUrl, privacyPolicyUrl } from '../constants/optInConstants';
import useBotAlertStyles from './BotAlert.styles';

const OptInConversation: FC<React.PropsWithChildren> = () => {
  const { locale } = useLocalization();
  const { accept, decline, state } = useAssistantOptIn();
  const { user: authenticatedUser } = useRobloxAuthentication();
  const { translate, translateHTML } = useTranslation();
  const {
    classes: { replyWrapper, reply, botReply, declineButton, optInDivider },
    cx,
  } = useConversationMessageStyles();
  const {
    classes: { alert },
  } = useBotAlertStyles();
  const botOptInPrompt = useMemo(
    () =>
      translateHTML(
        'Message.TermsOptIn',
        [
          {
            opening: 'tosLinkStart',
            closing: 'tosLinkEnd',
            // eslint-disable-next-line react/no-unstable-nested-components
            content: (linkText) => (
              <DynamicallyRouted.Link
                href={tosUrl}
                data-testid={TestId.OptInTOS}
                target='_blank'
                onClick={() =>
                  unifiedLoggerClient.logClickEvent({
                    eventName: DocsAssistantEvent.OptInTermsOfService,
                    parameters: {
                      locale: locale ? String(locale) : '',
                    },
                  })
                }>
                {linkText}
              </DynamicallyRouted.Link>
            ),
          },
          {
            opening: 'ppLinkStart',
            closing: 'ppLinkEnd',
            // eslint-disable-next-line react/no-unstable-nested-components
            content: (linkText) => (
              <DynamicallyRouted.Link
                href={privacyPolicyUrl}
                data-testid={TestId.OptInPP}
                target='_blank'
                onClick={() =>
                  unifiedLoggerClient.logClickEvent({
                    eventName: DocsAssistantEvent.OptInPrivacyPolicy,
                    parameters: {
                      locale: locale ? String(locale) : '',
                    },
                  })
                }>
                {linkText}
              </DynamicallyRouted.Link>
            ),
          },
        ],
        {
          username: authenticatedUser?.name ?? '',
        },
      ),
    [locale, authenticatedUser?.name, translateHTML],
  );

  const userInput = useMemo(() => {
    if (state === OptInState.DeclinedLoading || state === OptInState.Declined) {
      return <ConversationMessageByUser processedContent={translate('Message.OptInDeclineUser')} />;
    }
    return null;
  }, [state, translate]);

  const botResponse = useMemo(() => {
    if (state === OptInState.Declined) {
      return (
        <ConversationMessageByBot
          processedContent={translate('Message.OptInDeclineBot')}
          streaming={false}
        />
      );
    }
    if (state === OptInState.DeclinedLoading) {
      return <ConversationMessageByBot streaming />;
    }
    return null;
  }, [state, translate]);

  return (
    <Fragment>
      <article aria-label={translate('Label.Response')} className={replyWrapper}>
        <Grid container className={cx(reply, botReply)}>
          <Typography variant='body2'>{botOptInPrompt}</Typography>
          {(state === OptInState.SentPromptPending ||
            state === OptInState.WaitForInput ||
            state === OptInState.AcceptedLoading) && (
            <Fragment>
              <Divider className={optInDivider} />
              <Grid container justifyContent='flex-end'>
                <Button
                  disabled={state === OptInState.AcceptedLoading}
                  variant='contained'
                  onClick={decline}
                  color='primary'
                  classes={{ contained: declineButton }}>
                  {translate('Label.OptInDecline')}
                </Button>
                <Button
                  disabled={state === OptInState.AcceptedLoading}
                  onClick={accept}
                  variant='contained'
                  color='primary'
                  size='medium'>
                  {translate('Label.OptInAccept')}
                </Button>
              </Grid>
            </Fragment>
          )}
        </Grid>
      </article>
      {userInput}
      {botResponse}
      {state === OptInState.FailedToAccept && (
        <Alert className={alert} severity='error'>
          {translate('AlertMessage.Generic')}
        </Alert>
      )}
    </Fragment>
  );
};

export default OptInConversation;
